import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, CreditCard, Palette, Mail, Truck, Plug } from "lucide-react";
import { useTheme } from "next-themes";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CompanyProfileSection } from "./CompanyProfileSection";
import { CompanyLogoUpload } from "./CompanyLogoUpload";
import { PricingSettingsTab } from "./PricingSettingsTab";
import { EmailSettingsPanel } from "./EmailSettingsPanel";
import { StripeSubscriptionManager } from "./StripeSubscriptionManager";
import { TeamManagement } from "./TeamManagement";
import { SupplierIntegrationsPanel } from "./SupplierIntegrationsPanel";
import { ProjectMapItPanel } from "./ProjectMapItPanel";
import { GoodLeapSettings } from "./GoodLeapSettings";
import { EmployeesPanel } from "./EmployeesPanel";
import { CommissionRulesPanel } from "./CommissionRulesPanel";
import { PerTradeCommissionPanel } from "./PerTradeCommissionPanel";
import { RBRestorationTiersPanel } from "./RBRestorationTiersPanel";
import { TradesSettingsPanel } from "@/components/trades/TradesSettingsPanel";

interface OrgAdminSettingsProps {
  orgId: string | null;
}

export function OrgAdminSettings({ orgId }: OrgAdminSettingsProps) {
  const { theme, setTheme } = useTheme();
  return (
    <Tabs defaultValue="organization" className="space-y-6">
      <TabsList className="flex flex-wrap w-full">
        <TabsTrigger value="organization">Organization</TabsTrigger>
        <TabsTrigger value="trades">Trades</TabsTrigger>
        <TabsTrigger value="team">Team & Commissions</TabsTrigger>
        <TabsTrigger value="billing">Billing</TabsTrigger>
        <TabsTrigger value="pricing">My Rates</TabsTrigger>
        <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
        <TabsTrigger value="integrations">Integrations</TabsTrigger>
        <TabsTrigger value="email">Email</TabsTrigger>
        <TabsTrigger value="appearance">Appearance</TabsTrigger>
      </TabsList>

      <TabsContent value="organization" className="space-y-4">
        <CompanyLogoUpload />
        <CompanyProfileSection />
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Team Management
            </CardTitle>
            <CardDescription>
              Manage your team members and their roles
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TeamManagement orgId={orgId} />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="trades" className="space-y-4">
        <TradesSettingsPanel />
      </TabsContent>

      <TabsContent value="team" className="space-y-4">
        {orgId && <RBRestorationTiersPanel orgId={orgId} />}
        {orgId && <PerTradeCommissionPanel orgId={orgId} />}
        {orgId && <EmployeesPanel orgId={orgId} />}
        {orgId && <CommissionRulesPanel orgId={orgId} />}
      </TabsContent>

      <TabsContent value="billing" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription & Billing
            </CardTitle>
            <CardDescription>
              Manage your subscription plan and payment method
            </CardDescription>
          </CardHeader>
        </Card>

        <StripeSubscriptionManager />
      </TabsContent>

      <TabsContent value="pricing" className="space-y-4">
        <PricingSettingsTab />
      </TabsContent>

      <TabsContent value="suppliers" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Supplier Integrations
            </CardTitle>
            <CardDescription>
              Connect to supplier APIs for real-time pricing and product catalogs
            </CardDescription>
          </CardHeader>
        </Card>
        
        <SupplierIntegrationsPanel orgId={orgId} />
      </TabsContent>

      <TabsContent value="integrations" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plug className="w-5 h-5" />
              Partner Integrations
            </CardTitle>
            <CardDescription>
              Connect to partner platforms to extend your capabilities
            </CardDescription>
          </CardHeader>
        </Card>
        
        <ProjectMapItPanel orgId={orgId} />
        <GoodLeapSettings />
      </TabsContent>

      <TabsContent value="email" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Configuration
            </CardTitle>
            <CardDescription>
              Configure email settings for campaigns and notifications
            </CardDescription>
          </CardHeader>
        </Card>
        
        <EmailSettingsPanel />
      </TabsContent>

      <TabsContent value="appearance" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Theme Settings
            </CardTitle>
            <CardDescription>
              Customize the appearance of your workspace
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="theme-toggle">Dark Mode</Label>
              <Switch
                id="theme-toggle"
                checked={theme === "dark"}
                onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
              />
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
